// React hooks for data fetching from the mstream API, built on TanStack Query.
//
// Queries share a single app-wide cache (configured in main.tsx). Hooks return
// the native TanStack Query result shape — `{ data, isPending, isFetching,
// error, refetch, ... }` for queries and `{ mutate, mutateAsync, isPending,
// error, reset, ... }` for mutations.

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import {
  entitlementsApi,
  jobsApi,
  playgroundApi,
  schemaApi,
  schemasApi,
  scriptsApi,
  servicesApi,
  systemApi,
  testSuitesApi,
  tokensApi,
  workspacesApi,
} from "@/api/client";
import type {
  AssignTokenRequest,
  CreateTokenRequest,
  PlaygroundTestRequest,
  SchemaEntry,
  SchemaEntrySummary,
  ServicesListQuery,
  TestGenerateRequest,
  TestRunRequest,
  TestSuite,
  Workspace,
} from "@/types/api";
import {
  clearPipelineIntent,
  getPipelineIntentsSnapshot,
  overlayPipelineIntents,
  type PipelineIntent,
  reconcilePipelineIntents,
  setPipelineIntent,
  subscribePipelineIntents,
} from "./pipelineIntents";
import { queryKeys } from "./queryKeys";

export { queryKeys } from "./queryKeys";

// Default cadence for live job data. Jobs poll continuously so the UI converges
// on the backend's settled state even when start/stop is applied asynchronously
// (HA deployments) — the tracked start/stop intent bridges the gap until then.
const JOBS_POLL_MS = 5000;

// While a start/stop intent is pending we poll faster so the UI converges to
// the real backend state within ~1s instead of waiting up to a full JOBS_POLL_MS
// (the immediate post-mutation refetch can race ahead of the cluster and return
// the old state, so we need another look soon after). Reverts to JOBS_POLL_MS
// once every intent has converged.
const PENDING_POLL_MS = 800;

// ── Shared hook builders ──────────────────────────────────
//
// These collapse the repeated query/mutation wiring into one place, so each
// exported hook below is a one-line declaration of intent.

type QueryKey = readonly unknown[];

// A mutation that, on success, invalidates a query key derived from its
// variables — the common "write then refresh the affected list" pattern.
function useInvalidatingMutation<TVars, TResult>(
  mutationFn: (vars: TVars) => Promise<TResult>,
  invalidateKey: (vars: TVars) => QueryKey,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: invalidateKey(vars) }),
  });
}

// A job start/stop mutation: records the user's intended target so the badge
// shows the transition and the buttons disable immediately, clears it on error,
// and invalidates to converge on the backend's settled state.
//
// The transition is tracked as an *intent* (see pipelineIntents) rather than a
// cache-only optimistic write: in HA the backend applies start/stop — and even
// the `desired_state` flip — asynchronously, so the `onSettled` refetch can race
// in before the cluster has propagated the change and return the old settled
// state. A cache write would be clobbered by that refetch (re-enabling the
// button); the intent survives refetches and is cleared once the real `state`
// converges (see useJobs).
function useIntentJobMutation(
  mutationFn: (name: string) => Promise<unknown>,
  intent: PipelineIntent,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: (name: string) => {
      setPipelineIntent(name, intent);
    },
    onError: (_err, name) => {
      clearPipelineIntent(name);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
}

// A keyed query for a job sub-resource that polls while enabled.
function useJobSubresourcePolling<T>(
  jobName: string,
  key: QueryKey,
  fetcher: (name: string) => Promise<T>,
  options: { intervalMs: number; enabled: boolean },
) {
  return useQuery({
    queryKey: key,
    queryFn: () => fetcher(jobName),
    enabled: options.enabled && !!jobName,
    refetchInterval: options.intervalMs,
  });
}

// ── Jobs ──────────────────────────────────────────────────

// Coarse lifecycle of a query for UIs that render skeletons: "loading" only
// while the first fetch is genuinely unresolved, "error" once it has failed
// with nothing to show (so skeletons don't spin forever on a dead backend),
// and "ready" whenever there is data — stale data during a failing refetch
// still counts as ready.
export type QueryPhase = "loading" | "error" | "ready";

export function queryPhase(data: unknown, error: unknown): QueryPhase {
  if (data != null) return "ready";
  return error ? "error" : "loading";
}

// Single source of truth for the jobs list. Polls so every consumer (list,
// dashboard, single-pipeline detail) stays live and converges after mutations.
export function useJobs() {
  const intents = useSyncExternalStore(
    subscribePipelineIntents,
    getPipelineIntentsSnapshot,
  );
  const hasPendingIntent = Object.keys(intents).length > 0;

  const query = useQuery({
    queryKey: queryKeys.jobs,
    queryFn: () => jobsApi.list(),
    refetchInterval: hasPendingIntent ? PENDING_POLL_MS : JOBS_POLL_MS,
  });

  // Prune intents the backend has now satisfied (or that failed) so a stale
  // intent can't keep overriding a job that later changes state externally.
  useEffect(() => {
    if (query.data) reconcilePipelineIntents(query.data);
  }, [query.data]);

  const data = useMemo(
    () =>
      query.data ? overlayPipelineIntents(query.data, intents) : query.data,
    [query.data, intents],
  );

  return { ...query, data };
}

/** A running job's checkpoints move, so they are read again every
 *  `intervalMs` while it runs; a stopped one is read once. */
export function useJobCheckpoints(jobName: string, intervalMs?: number) {
  return useQuery({
    queryKey: queryKeys.jobCheckpoints(jobName),
    queryFn: () => jobsApi.getCheckpoints(jobName),
    enabled: !!jobName,
    refetchInterval: intervalMs ?? false,
  });
}

export function useJobMetrics(jobName: string) {
  return useQuery({
    queryKey: queryKeys.jobMetrics(jobName),
    queryFn: () => jobsApi.getMetrics(jobName),
    enabled: !!jobName,
  });
}

export function useJobMetricsPolling(
  jobName: string,
  intervalMs: number = JOBS_POLL_MS,
  enabled: boolean = true,
) {
  return useJobSubresourcePolling(
    jobName,
    queryKeys.jobMetrics(jobName),
    (name) => jobsApi.getMetrics(name),
    { intervalMs, enabled },
  );
}

export function useJobWorkersPolling(
  jobName: string,
  intervalMs: number = JOBS_POLL_MS,
  enabled: boolean = true,
) {
  return useJobSubresourcePolling(
    jobName,
    queryKeys.jobWorkers(jobName),
    (name) => jobsApi.getWorkers(name),
    { intervalMs, enabled },
  );
}

export const useRestartJob = () =>
  useIntentJobMutation((name) => jobsApi.restart(name), "Running");

export const useStopJob = () =>
  useIntentJobMutation((name) => jobsApi.stop(name), "Stopped");

export const useDeleteJob = () =>
  useInvalidatingMutation(
    ({ name, discardBacklog }: { name: string; discardBacklog?: boolean }) =>
      jobsApi.delete(name, { discardBacklog }),
    () => queryKeys.jobs,
  );

// ── Workspaces ────────────────────────────────────────────

// The workspaces list endpoint returns summaries ({id, name}) only, but the
// list page shows each workspace's pipeline and script count — so the full
// records are fetched per id and returned together. One unretrievable
// record drops out of the list instead of failing the whole page.
export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: async () => {
      const summaries = await workspacesApi.list();
      const results = await Promise.allSettled(
        summaries.map((s) => workspacesApi.get(s.id)),
      );
      return results.flatMap((r) =>
        r.status === "fulfilled" ? [r.value] : [],
      );
    },
  });
}

export const useUpdateWorkspace = () =>
  useInvalidatingMutation(
    (vars: { id: string; payload: Partial<Workspace> & { name: string } }) =>
      workspacesApi.update(vars.id, vars.payload),
    () => queryKeys.workspaces,
  );

export const useCreateWorkspace = () =>
  useInvalidatingMutation(
    (payload: Partial<Workspace> & { name: string }) =>
      workspacesApi.create(payload),
    () => queryKeys.workspaces,
  );

export const useDeleteWorkspace = () =>
  useInvalidatingMutation(
    (id: string) => workspacesApi.delete(id),
    () => queryKeys.workspaces,
  );

// ── Services ──────────────────────────────────────────────

export function useServices(query: ServicesListQuery = {}) {
  return useQuery({
    queryKey: queryKeys.services(query),
    queryFn: () => servicesApi.list(query),
    // Same cadence as jobs, so service lists (and the LLM is_active flag,
    // which only the services response carries) stay live without a refocus.
    refetchInterval: JOBS_POLL_MS,
  });
}

export function useService(name: string) {
  return useQuery({
    queryKey: queryKeys.service(name),
    queryFn: () => servicesApi.get(name),
    enabled: !!name,
  });
}

export function useServiceResources(serviceName: string) {
  return useQuery({
    queryKey: queryKeys.serviceResources(serviceName),
    queryFn: () => servicesApi.listResources(serviceName),
    enabled: !!serviceName,
  });
}

export const useRemoveService = () =>
  useInvalidatingMutation(
    (name: string) => servicesApi.remove(name),
    () => queryKeys.servicesRoot,
  );

export const useActivateService = () =>
  useInvalidatingMutation(
    (name: string) => servicesApi.activate(name),
    () => queryKeys.servicesRoot,
  );

// ── Tokens ────────────────────────────────────────────────

export function useTokens(serviceName: string | null) {
  return useQuery({
    queryKey: queryKeys.tokens(serviceName ?? ""),
    queryFn: () => tokensApi.list(serviceName as string),
    enabled: !!serviceName,
  });
}

export const useCreateToken = () =>
  useInvalidatingMutation(
    (vars: { serviceName: string; req: CreateTokenRequest }) =>
      tokensApi.create(vars.serviceName, vars.req),
    (vars) => queryKeys.tokens(vars.serviceName),
  );

export const useRevokeToken = () =>
  useInvalidatingMutation(
    (vars: { serviceName: string; tokenId: string }) =>
      tokensApi.revoke(vars.serviceName, vars.tokenId),
    (vars) => queryKeys.tokens(vars.serviceName),
  );

export const useAssignToken = () =>
  useInvalidatingMutation(
    (vars: { serviceName: string; tokenId: string; req: AssignTokenRequest }) =>
      tokensApi.assign(vars.serviceName, vars.tokenId, vars.req),
    (vars) => queryKeys.tokens(vars.serviceName),
  );

export const useUnassignToken = () =>
  useInvalidatingMutation(
    (vars: { serviceName: string; tokenId: string }) =>
      tokensApi.unassign(vars.serviceName, vars.tokenId),
    (vars) => queryKeys.tokens(vars.serviceName),
  );

// ── System ────────────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => systemApi.health(),
  });
}

export function useSystemStats() {
  return useQuery({
    queryKey: queryKeys.systemStats,
    queryFn: () => systemApi.stats(),
  });
}

export function useSystemStatsPolling(intervalMs: number = JOBS_POLL_MS) {
  return useQuery({
    queryKey: queryKeys.systemStats,
    queryFn: () => systemApi.stats(),
    refetchInterval: intervalMs,
  });
}

export function useHealthPolling(intervalMs: number = 10000) {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => systemApi.health(),
    refetchInterval: intervalMs,
  });
}

// 30 min matches the cert's `refresh_after` cadence — polling faster would
// just hit the same snapshot until the refresher updates it.
export function useEntitlementsPolling(intervalMs: number = 30 * 60 * 1000) {
  return useQuery({
    queryKey: queryKeys.entitlements,
    queryFn: () => entitlementsApi.get(),
    refetchInterval: intervalMs,
  });
}

// ── Scripts ───────────────────────────────────────────────

export function useScripts(serviceName: string) {
  return useQuery({
    queryKey: queryKeys.scripts(serviceName),
    queryFn: () => scriptsApi.list(serviceName),
    enabled: !!serviceName,
  });
}

export function useScript(serviceName: string, filename: string) {
  return useQuery({
    queryKey: queryKeys.script(serviceName, filename),
    queryFn: () => scriptsApi.get(serviceName, filename),
    enabled: !!serviceName && !!filename,
  });
}

// Script filenames per service, for existence checks across several services
// at once (the Workspaces script counts). Services whose list hasn't loaded
// (or failed) simply have no entry — callers treat that as "can't verify".
// The caller must pass a referentially stable array (memoized).
export function useScriptFilenames(serviceNames: string[]) {
  return useQueries({
    queries: serviceNames.map((name) => ({
      queryKey: queryKeys.scripts(name),
      queryFn: () => scriptsApi.list(name),
    })),
    combine: (results) => {
      const map = new Map<string, Set<string>>();
      results.forEach((result, i) => {
        if (result.data) {
          map.set(serviceNames[i], new Set(result.data.map((s) => s.filename)));
        }
      });
      return map;
    },
  });
}

// Per-job metrics for the whole fleet at once (the dashboard's Health
// totals). Shares queryKeys.jobMetrics with the pipeline page, so the
// caches feed each other; polls on the jobs cadence. The caller must pass
// a referentially stable array (memoized). `answered` counts the jobs
// whose metrics have SETTLED — with data or with an error — so a fleet
// whose metrics all fail still leaves the loading state (and shows the
// zero totals) instead of holding skeletons forever.
export function useFleetMetrics(jobNames: string[]) {
  return useQueries({
    queries: jobNames.map((name) => ({
      queryKey: queryKeys.jobMetrics(name),
      queryFn: () => jobsApi.getMetrics(name),
      refetchInterval: JOBS_POLL_MS,
    })),
    combine: (results) => ({
      metrics: results.map((r) => r.data),
      answered: results.filter((r) => r.data !== undefined || r.isError).length,
    }),
  });
}

// ── Playground / test ─────────────────────────────────────

export const usePlaygroundTest = () =>
  useMutation({
    mutationFn: (request: PlaygroundTestRequest) => playgroundApi.test(request),
  });

export const usePlaygroundValidate = () =>
  useMutation({
    mutationFn: (request: { script: string }) =>
      playgroundApi.validate(request),
  });

export const useTestGenerate = () =>
  useMutation({
    mutationFn: (request: TestGenerateRequest) =>
      playgroundApi.testGenerate(request),
  });

export const useTestRun = () =>
  useMutation({
    mutationFn: (request: TestRunRequest) => playgroundApi.testRun(request),
  });

// ── Schemas ───────────────────────────────────────────────

// Generate a sample document for a resource: fetch its schema, then fill it.
export const useGenerateSample = () =>
  useMutation({
    mutationFn: async (vars: { serviceName: string; resource: string }) => {
      const variants = await schemaApi.getSchema(
        vars.serviceName,
        vars.resource,
      );
      if (!variants || variants.length === 0) {
        throw new Error("No schema variants found for this resource");
      }
      const filledDoc = await schemaApi.fill(variants[0].schema);
      return JSON.stringify(filledDoc, null, 2);
    },
  });

// Schema preview. Native mutation `reset()` clears the last preview.
export const useSchemaPreview = () =>
  useMutation({
    mutationFn: (vars: {
      serviceName: string;
      resource: string;
      sampleSize?: number;
    }) => schemaApi.getSchema(vars.serviceName, vars.resource, vars.sampleSize),
  });

// ── Test suites ───────────────────────────────────────────

export function useTestSuites() {
  return useQuery({
    queryKey: queryKeys.testSuites,
    queryFn: () => testSuitesApi.list(),
  });
}

export function useTestSuite(id: string | null) {
  return useQuery({
    queryKey: queryKeys.testSuite(id ?? ""),
    queryFn: () => testSuitesApi.get(id as string),
    enabled: !!id,
  });
}

export const useSaveTestSuite = () =>
  useInvalidatingMutation(
    (suite: Omit<TestSuite, "id"> & { id?: string }) =>
      testSuitesApi.save(suite),
    () => queryKeys.testSuites,
  );

export const useDeleteTestSuite = () =>
  useInvalidatingMutation(
    (id: string) => testSuitesApi.delete(id),
    () => queryKeys.testSuites,
  );

// ── Schema persistence ────────────────────────────────────

export function useSchemas(serviceName: string | null) {
  return useQuery({
    queryKey: queryKeys.schemas(serviceName ?? ""),
    queryFn: () => schemasApi.list(serviceName as string),
    enabled: !!serviceName,
  });
}

/** A stored schema's list entry with the service whose store holds it —
 *  schemas live per service (TTTL-319). */
export interface ServiceSchemaEntry extends SchemaEntrySummary {
  serviceName: string;
}

/** One list query per service (a referentially stable array), combined:
 *  the entries of every service that answered, each tagged with its
 *  service, plus each service's own state for the explorer's folders. */
export function useSchemasByService(serviceNames: string[]): {
  entries: ServiceSchemaEntry[];
  byService: Record<
    string,
    { data?: SchemaEntrySummary[]; isLoading: boolean; isError: boolean }
  >;
} {
  const results = useQueries({
    queries: serviceNames.map((serviceName) => ({
      queryKey: queryKeys.schemas(serviceName),
      queryFn: () => schemasApi.list(serviceName),
    })),
    combine: combineServiceSchemas,
  });
  return useMemo(() => {
    const byService: ReturnType<typeof useSchemasByService>["byService"] = {};
    const entries: ServiceSchemaEntry[] = [];
    serviceNames.forEach((serviceName, i) => {
      const r = results[i];
      byService[serviceName] = r;
      for (const entry of r.data ?? []) entries.push({ ...entry, serviceName });
    });
    return { entries, byService };
  }, [serviceNames, results]);
}

function combineServiceSchemas(
  results: Array<{
    data?: SchemaEntrySummary[];
    isLoading: boolean;
    isError: boolean;
  }>,
): Array<{
  data?: SchemaEntrySummary[];
  isLoading: boolean;
  isError: boolean;
}> {
  return results.map((r) => ({
    data: r.data,
    isLoading: r.isLoading,
    isError: r.isError,
  }));
}

export function useSchema(serviceName: string | null, id: string | null) {
  return useQuery({
    queryKey: queryKeys.schema(serviceName ?? "", id ?? ""),
    queryFn: () => schemasApi.get(serviceName as string, id as string),
    enabled: !!serviceName && !!id,
  });
}

// Saving refreshes the service's list AND the entry's own cache, so a
// row that re-reads the stored definition (the deploy gate, the
// already-saved check) sees the new text rather than the last fetch.
export function useSaveSchema() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      serviceName: string;
      entry: Omit<SchemaEntry, "id"> & { id?: string };
    }) => schemasApi.save(vars.serviceName, vars.entry),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schemas(vars.serviceName),
      });
      if (vars.entry.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.schema(vars.serviceName, vars.entry.id),
        });
      }
    },
  });
}

// The stored definitions of several schema entries at once (the deploy
// gate's check of attached schemas, TTTL-216), each from its own
// service's store. Keyed by the Studio's schema row id
// ("schema:<service>:<entry id>"): the text once fetched, null when the
// fetch failed (nothing to verify against), absent while a fetch is in
// flight. The caller must pass a referentially stable array (memoized).
function combineSchemaDefinitions(
  results: Array<{ data?: SchemaEntry; isError: boolean }>,
): Array<string | null | undefined> {
  return results.map((r) => {
    if (r.data) {
      return typeof r.data.definition === "string" ? r.data.definition : null;
    }
    return r.isError ? null : undefined;
  });
}

export function useSchemaDefinitions(
  refs: Array<{ serviceName: string; entryId: string }>,
): Record<string, string | null> {
  // With a stable combine, useQueries structurally shares the combined
  // array, so the memo below only re-runs when a definition changes.
  const definitions = useQueries({
    queries: refs.map((ref) => ({
      queryKey: queryKeys.schema(ref.serviceName, ref.entryId),
      queryFn: () => schemasApi.get(ref.serviceName, ref.entryId),
    })),
    combine: combineSchemaDefinitions,
  });
  return useMemo(() => {
    const map: Record<string, string | null> = {};
    refs.forEach((ref, i) => {
      if (definitions[i] !== undefined) {
        map[`schema:${ref.serviceName}:${ref.entryId}`] = definitions[i];
      }
    });
    return map;
  }, [refs, definitions]);
}

export const useDeleteSchema = () =>
  useInvalidatingMutation(
    (vars: { serviceName: string; id: string }) =>
      schemasApi.delete(vars.serviceName, vars.id),
    (vars) => queryKeys.schemas(vars.serviceName),
  );
